import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget.
 *
 * Usage:
 *   const [token, setToken] = useState("");
 *   <TurnstileWidget onVerify={setToken} />
 *
 * The widget renders nothing if REACT_APP_TURNSTILE_SITE_KEY is unset.
 * Cloudflare's official test site key "1x00000000000000000000AA" always
 * resolves successfully — used in dev. Replace with a real site key in prod.
 */
export function TurnstileWidget({ onVerify, theme = "light", size = "normal" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Load Cloudflare script once
  useEffect(() => {
    if (!siteKey) return;
    if (window.turnstile) { setLoaded(true); return; }
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      existing.addEventListener("load", () => setLoaded(true));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => setLoaded(true);
    document.head.appendChild(s);
  }, [siteKey]);

  // Render the widget
  useEffect(() => {
    if (!loaded || !containerRef.current || widgetIdRef.current || !siteKey) return;
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        size,
        callback: (token) => onVerify?.(token),
        "error-callback": () => onVerify?.(""),
        "expired-callback": () => onVerify?.(""),
      });
    } catch (e) {
      // No-op: widget will simply not show, auth still works (server falls back)
    }
    return () => {
      try {
        if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
      } catch (e) {}
      widgetIdRef.current = null;
    };
  }, [loaded, siteKey, onVerify, theme, size]);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" className="mt-4"/>;
}

export default TurnstileWidget;
